// Need to use the React-specific entry point to import createApi
import { EventSource } from 'eventsource'

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { prepareDefaultHeaders } from '.'
import { LiveEventGameAIPrompt } from '../features/live-events/types/LiveEventGameAIPrompt'
import keycloakService from '../services/KeycloakService'

const REDUCER_PATH = 'liveEventPredictionsApi'

export enum SSEStreamStatus {
  FETCHING = 'fetching',
  STREAMING = 'streaming',
  CLOSED = 'closed',
}

export type SSEStreamData = {
  data: string | null
  imageUrl: string | null
  status: SSEStreamStatus
}

enum StreamDataType {
  IMAGE = 'IMAGE',
  TEXT = 'TEXT',
}

export type GameAvailableAISummaries = {
  gameId: string
  prompts: LiveEventGameAIPrompt[]
}

export const liveEventPredictionsApi = createApi({
  reducerPath: REDUCER_PATH,
  baseQuery: fetchBaseQuery({
    baseUrl: window.GR_API_URLS.API_URL_LIVE_EVENTS_PREDICTIONS,
    prepareHeaders: prepareDefaultHeaders,
  }),
  endpoints: (builder) => ({
    getAvailableAISummariesForGames: builder.query<GameAvailableAISummaries[], void>({
      query: () => ({
        url: `/summary/games`,
      }),
    }),
    getLiveEventAISummaryStream: builder.query<SSEStreamData, { gameId: string; prompt: LiveEventGameAIPrompt }>({
      queryFn: () => ({ data: { data: null, imageUrl: null, status: SSEStreamStatus.FETCHING } }),
      keepUnusedDataFor: 0,
      async onCacheEntryAdded({ gameId, prompt }, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
        await cacheDataLoaded
        await prepareDefaultHeaders()

        const source = new EventSource(`${window.GR_API_URLS.API_URL_LIVE_EVENTS_PREDICTIONS}/summary/stream/${gameId}/prompt/${prompt}`, {
          fetch: (input, init) =>
            fetch(input, {
              ...init,
              headers: {
                ...init.headers,
                Authorization: `Bearer ${keycloakService.token}`,
              },
            }),
        })

        source.onmessage = (event) => {
          try {
            const newData: { type: StreamDataType; data: string } = JSON.parse(event.data)

            updateCachedData((stream) => {
              if (newData.type === StreamDataType.IMAGE) {
                return { data: '', imageUrl: newData.data, status: SSEStreamStatus.STREAMING }
              }

              return { data: stream.data + newData.data, imageUrl: stream.imageUrl, status: SSEStreamStatus.STREAMING }
            })
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to parse SSE:', e)
          }
        }

        source.onerror = () => {
          source.close()
          updateCachedData((stream) => {
            return { data: stream.data, imageUrl: stream.imageUrl, status: SSEStreamStatus.CLOSED }
          })
        }

        await cacheEntryRemoved
        source.close()
      },
    }),
  }),
})

export const { useGetLiveEventAISummaryStreamQuery, useGetAvailableAISummariesForGamesQuery } = liveEventPredictionsApi
